import React, { useState, useContext, useEffect,  useRef } from 'react';
import { useHistory } from 'react-router-dom';

import {Grid, Paper, Box, Stepper, Step, StepLabel, Typography, TextField, Button, Snackbar, Grow } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import LockResetIcon from '@mui/icons-material/LockReset';

import { LoginContext } from '../helpers/Context';
import valuationsWebApi from '../services/valuationsWebApi';
import useForm from '../hooks/useForm';
import useFetch from '../hooks/useFetch';

import useAxios from '../hooks/useAxios';
import DialogModal from '../components/modals/DialogModal';


const useStyles = makeStyles((mainTheme) => ({
  gridContainerStyle:{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '85vh', 
    [mainTheme.breakpoints.down('lg')]: {
      minHeight:"85vh",
    },
    marginTop: "64px",
    padding:"0px",
  },
  paperStyle: {
    // display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 350,
    // width:"100%",
    height: 420,
    margin: '10px',
    backgroundColor:mainTheme.palette.secondary.main,
    padding: mainTheme.spacing(2),
  },
  customLabelStyle: {
    fontSize: "12px"
  },
  iconBox:{
    width:'100%',
    textAlign: 'center',
    AlignItems: 'center',
  },
  iconStyle:{
    color:mainTheme.palette.primary.main,
  },
  textStyle:{
    // color:mainTheme.palette.primary.main,
    color:"black"
  },
  buttonStyle:{
    color: "white", //mainTheme.palette.primary.main,
    textTransform:"none",
    fontSize: 12,
    margin: "3px",
    backgroundColor:mainTheme.palette.primary.main,
    "&:hover": {
      color:mainTheme.palette.secondary.main,
      backgroundColor:mainTheme.palette.primary.main,
    },
    "&:disabled": {
      color:"gray",
      backgroundColor:mainTheme.palette.primary.main,
    },
  },
  loginLinkStyle:{
    marginTop:"5px",
    marginLeft:"5px",
    fontSize:12,
    color:"blue"
  }
  }));

const ResetPassword = () => {
  const classes = useStyles();
  const { userData, setUserData } = useContext(LoginContext);
  const { userId } = userData;
  const steps = ['Provide your e-mail', 'Input the code sent to you', 'Create a new password'];
  const [activeStep, setActiveStep] = useState(0);
  const [code, setCode] = useState({generatedCode:"", inputCode:""});
  const [ isDialogOpen, setIsDialogOpen ] = useState(false);
  const [ dialogOptions, setDialogOptions ] = useState({severity:"",title:"",message:"",buttons:{},action:""});
  const [codeError, setCodeError]=useState("");
  const { axiosFetch: postResetEmail } = useAxios();   
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarText, setSnackbarMessage] = useState("");
  const {handleChange, noBlanks, isValidCurrUser,registerData, setRegisterData, formErrors, setFormErrors } = useForm ();
  const { updatePassword, createSession } = useFetch({setIsSnackbarOpen, setSnackbarMessage})
  const idTextFieldRef = useRef(null);
  const codeTextFieldRef = useRef(null);
  const passwordTextFieldRef = useRef(null);
  const history = useHistory();

  const focusOnTextField = (step) => {
    switch (step) {
      case 0:
        idTextFieldRef.current.focus();
        break;
      case 1:
        codeTextFieldRef.current.focus();
        break;
      case 2:
        passwordTextFieldRef.current.focus();
        break;
      default:
        break;
    }
  };

  const checkFormErrors=()=>{
    if (formErrors.id !=="" || formErrors.password !=="" ){
      return true
    }
    return false
  }
  
  const handleSnackbarClose=()=>{
    setIsSnackbarOpen(false);
    setSnackbarMessage("")
  }

  const handleDialogClose=()=>{
    setDialogOptions({severity:"",title:"",message:"",buttons:{},action:""});
    setIsDialogOpen(false);
  }

  const postResetSuccessCb=()=>{
    setIsEmailSent(true);
  }

  const postResetErrorCb=(error)=>{
    setIsEmailSent(false);
    setDialogOptions({severity:"error", title:"Error", message:"There was a problem sending the code to your email account. Please try later.",buttons:{button1:"Ok"}})
    setIsDialogOpen (true);
  }

  const handleSendEmailToUser=()=>{
    const code = generateCode();
    postResetEmail({ axiosInstance: valuationsWebApi, method: 'POST', url: '/reset-email', data: { userId: registerData.id, code : code  }, requestConfig: { headers: {'Authorization': userId,},}},postResetSuccessCb, postResetErrorCb);
  }

  const generateShortHash=()=> {
    const min = 100000; 
    const max = 999999; 
    const randomHash = Math.floor(Math.random() * (max - min + 1)) + min;
    return randomHash.toString(); 
  }

  const generateCode=()=>{
    const hash = generateShortHash()
    setCode (prevState => ({...prevState, generatedCode:hash }))
    return hash
  }

  const handleChangeInputCode=(e)=>{
    const currentCode = e.target.value;
    setCode (prevState => ({...prevState, inputCode:currentCode }));
    if (code.inputCode === code.generatedCode){
      setCodeError("");
    } else {
        if (currentCode.length === 6 && (currentCode !== code.generatedCode)){
          setCodeError("The code you provided is not the same that we've sent you by email");  
        } else {
          if (currentCode.length > 6){
            setCodeError("The code you provided has more than 6 digits");  
          } else {
            if (currentCode.length <= 6){
              setCodeError("")
            }
          }
        }
      }
  }

  const handleInputCode=()=>{
    if (code.inputCode === code.generatedCode){
      setCodeError("");
    } else {
      if (code.inputCode.length < 6){
        setCodeError("The code you provided does not have 6 digits");  
      } else {
        if (code.inputCode.length > 6){
          setCodeError("The code you provided has more than 6 digits");  
        } else {
          setCodeError("The code you provided is not the same that we've sent you by email");
        } 
      }
    }
  }

  const checkRequiredFields=()=>{
    if (registerData.id !=="" && registerData.password !=="" && registerData.confirmPassword !==""){
      return true
    }
    return false
  }

  const checkRequiredEmail=()=>{
    if (registerData.id !==""){
      return true
    }
    return false
  }

  const validatePasswords=()=>{
    if (registerData.password !=="" && registerData.confirmPassword !=="" && registerData.password !== registerData.confirmPassword) {
      setFormErrors (prevState => ({...prevState, password:'Passwords do not match', confirmPassword:"Passwords do not match"} ))
    } else {
      setFormErrors (prevState => ({...prevState, password:"", confirmPassword:""}))
    }
  };

  const handleFinish=()=>{
    const dataToProcess = {id: registerData.id, password: registerData.password }
    createSession(dataToProcess)
    setRegisterData({ id:"", password:"", confirmPassword:"", product:"",firstName:"",lastName:"", country:"", countryName:"", phone:"", birthday:null, occupation:"", company:"",email:"", city:"", description:"", website:"", twitter:"", linkedin:"", facebook:"", instagram:"", youtube:"", tiktok:"", photo:"", banner:""})
  }

  const handleNext=()=>{
    if (activeStep===2){
      handleUpdate()
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack=()=>{
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleIdBlur=(e)=>{
    handleChange (e,setRegisterData,[isValidCurrUser]);
  }

  const handleCancel=()=>{
    history.goBack()
  }

  const handleUpdate=()=>{
    if (! checkFormErrors() && checkRequiredFields()) {
      const {id, password} = registerData
      updatePassword(id, password); 
    } else {
      setDialogOptions({severity:"error", title:"Error", message:"There was a problem saving the data on the server. Please try later.",buttons:{button1:"Ok"}})
      setIsDialogOpen (true);
      // console.log("Not saved in database")
    }
  };

  useEffect(() => {
    focusOnTextField(activeStep);
  }, [activeStep]);

  useEffect(() => {
    if (userId !=="" && userId!==null && userId!==undefined){
      setRegisterData (prevState => ( {...prevState, id: userId}));
      // setActiveStep(1);
      // const code = generateCode();
      // postResetEmail({ axiosInstance: valuationsWebApi, method: 'POST', url: '/reset-email', data: { userId: registerData.id, code : code  }, requestConfig: { headers: {'Authorization': userId,},}},postResetSuccessCb, postResetErrorCb);
    }
  }, []);


  const renderStepContent=(step)=>{
    switch (step) {
      case 0:
        return (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              { userId? 
              <Typography align ="center" gutterBottom className={classes.textStyle} sx={{marginTop:"10px",marginBottom:"10px", fontSize:"12px"}}>
                {/* We are going to send you an email to your e-mail account, with a code to create a new password</Typography> */}
                We'll be sending an email to your registered account shortly. It will contain a unique code that you can use to set up a new password.</Typography>
              : 
              
              <Typography align ="center" gutterBottom className={classes.textStyle} sx={{marginTop:"10px",marginBottom:"10px", fontSize:"12px"}}>
                {/* Please provide your registered e-mail account, so we can send you a code to create a new password</Typography> */}
                Please provide us with your registered email address, so we can promptly send you a code to generate a new password.</Typography>
              }
               {userId? <>
                <TextField 
                variant ="filled" margin="dense" size="small" fullWidth 
                // disabled
                // variant ="outlined" margin="dense" size="small" fullWidth
                label="Your E-mail *"
                name="id"
                value={registerData.id}
                autofocus
                autoComplete="email"
                // onChange={ (e) => {handleChange (e,setRegisterData,[noBlanks])}}
                // onBlur={(e) => handleIdBlur(e)}
                inputProps={{style: {fontSize: 14}}} 
                error={formErrors.id}
                inputRef={idTextFieldRef}
              />
              {/* {formErrors.id ? <div className="error-helper-text">{formErrors.id}</div> : null}                */}
              </>
              :<>
              <TextField 
                variant ="filled" margin="dense" size="small" fullWidth  
                // variant ="outlined" margin="dense" size="small" fullWidth
                label="Your E-mail *"
                name="id"
                value={registerData.id}
                autofocus
                autoComplete="email"
                onChange={ (e) => {handleChange (e,setRegisterData,[noBlanks])}}
                onBlur={(e) => handleIdBlur(e)}
                inputProps={{style: {fontSize: 14}}} 
                error={formErrors.id}
                inputRef={idTextFieldRef}
              />
              {formErrors.id ? <div className="error-helper-text">{formErrors.id}</div> : null}
              </>
              }

            </Grid>
            <Box sx={{display:"flex", alignItems:"left", justifyContent:"flex-start", width:"100%", marginLeft:"2px" }}>
              <Button disableRipple className={classes.buttonStyle} disabled={checkFormErrors() || ! checkRequiredEmail() || isEmailSent} onClick={handleSendEmailToUser}>
                Send the code to my E-mail
              </Button>
            </Box>
            <Box sx={{display:"flex", alignItems:"center", justifyContent:"center", width:"100%" }}>
              { isEmailSent && registerData.id !==""? 
                <Typography align ="center" gutterBottom className={classes.textStyle} sx={{marginTop:"5px",marginBottom:"5px", fontSize:"11px"}}>
                  {`The code was sent to ${registerData.id}. If you don't find it, please also check in your junk e-mail box.`}</Typography> : null}
            </Box>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <Typography align ="center" gutterBottom className={classes.textStyle} sx={{marginTop:"10px",marginBottom:"0px", fontSize:"13px"}} >
                {`Please include the 6 digits code we've sent you by email at ${registerData.id}`}</Typography>
            </Grid> 
            {/* { code.generatedCode? <p>{code.generatedCode}</p> : null}    */}
            <Grid item xs={12} sm={12}>
              <TextField 
                autoFocus 
                variant ="filled" margin="dense" size="small"  
                // variant ="outlined" margin="dense" size="small" 
                fullWidth
                label="Input the code"
                name="code.inputCode"
                value={code.inputCode}
                autofocus
                autoComplete="code"
                onChange={ (e) => {handleChangeInputCode (e) }}
                onBlur={(e) => handleInputCode(e)}
                inputProps={{style: {fontSize: 14}}} 
                inputRef={codeTextFieldRef}
                error={codeError}
              />
              {codeError ? <div className="error-helper-text">{codeError}</div> : null}
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={2} > 
            <Grid item xs={12}>
              <Typography align ="center" gutterBottom className={classes.textStyle} sx={{marginTop:"10px",marginBottom:"10px", fontSize:"12px"}}>
                  Now you can choose a new password </Typography>
              <TextField 
              variant ="filled" margin="dense" size="small" 
                // variant ="outlined" margin="dense" size="small" 
                fullWidth
                label="Create Your Password *"
                name="password"
                type="password"
                value={registerData.password}
                autoComplete="new-password"
                onChange={(e) => { handleChange (e,setRegisterData,[noBlanks])}}
                onBlur={validatePasswords}
                inputProps={{style: {fontSize: 14}}} 
                inputRef={passwordTextFieldRef}
                error={formErrors.password}
              />
              {formErrors.password ? <div className="error-helper-text">{formErrors.password}</div> : null}
            </Grid>

          <Grid item xs={12}>
            <TextField 
            variant ="filled" margin="dense" size="small"  
              // variant ="outlined" margin="dense" size="small" 
              fullWidth
              label="Confirm Your Password *"
              name="confirmPassword"
              type="password"
              value={registerData.confirmPassword}
              autoComplete="confirm-password"
              onChange={ (e) => {handleChange (e,setRegisterData,[noBlanks])}}
              onBlur={validatePasswords}
              inputProps={{style: {fontSize: 14}}} 
              error={formErrors.confirmPassword}
            />
            {formErrors.confirmPassword ? <div className="error-helper-text">{formErrors.confirmPassword}</div> : null}
          </Grid>
        </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <>
    <Grid container alignItems="center" justifyContent="center" spacing={1}  className={classes.gridContainerStyle}  >
    {/* <Container maxWidth="xs">   */}
    <Grid item>
      <Grow in timeout = {1000}>
        <Paper elevation={12} className={classes.paperStyle} style={{paddingBottom: "10px"}} >
          <Box style={{display:"flex", flexDirection:"column", height:"100%", justifyContent:"space-between"}}>
            <Box>
            <Typography align='center' gutterBottom className={classes.textStyle} >Reset Password</Typography>  
            <Box className={classes.iconBox} >
              <LockResetIcon className={classes.iconStyle} style={{ fontSize: 40 }}/>
            </Box>
              <Stepper activeStep={activeStep} alternativeLabel  > 
                {steps.map((label) => (
                  <Step key={label} >
                    <StepLabel classes={{label: classes.customLabelStyle}} >{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              <Box style={{height:"5px"}}/>
              {activeStep === steps.length ? (
                <Grid container spacing={1}>
                  <Grid item container display="flex" alignItems="center" justifyContent="center" xs={12} style={{marginBottom:"8px"}}>
                  {/* <CheckCircleOutlineIcon className={classes.iconStyle} style={{ fontSize: 40 }} /> */}
                    <Typography variant="body2" align="center" gutterBottom className={classes.textStyle} sx={{marginTop:"15px"}}><b>Success! Your password has been reset</b></Typography>
                    <Typography variant="caption" align="justify" paragraph className={classes.textStyle} >
                      You can now log in to your account using your new password. If you have any further questions or encounter any issues, please don't hesitate to contact our support team.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} display="flex" alignItems="center" justifyContent="center" style={{paddingTop:"0px"}}>
                    <Button variant="contained" className={classes.buttonStyle} disableRipple color="primary" onClick={handleFinish}>
                      Go to main page
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <>
                <form>
                  {renderStepContent(activeStep)}
                </form>
                </>
              )}
            </Box>
            {activeStep !== steps.length ? ( 
              <Box>
                <Grid container item direction="row" alignItems="flex-end" justifyContent="flex-end" >
                  {activeStep > 0 ?
                    <Button disableRipple className={classes.buttonStyle} disabled={activeStep === 0} onClick={handleBack} >
                      Back
                    </Button>
                  : 
                    <Button disableRipple className={classes.buttonStyle} onClick={handleCancel} >
                      Cancel
                    </Button>
                  }
                  <Button disableRipple className={classes.buttonStyle} disabled={checkFormErrors() || registerData.id==="" || ! isEmailSent || (activeStep===1 && (codeError!=="" || code.inputCode.length < 6 || code.inputCode.length > 6) ) } onClick={handleNext}>
                    {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                  </Button>
                </Grid> 
              </Box>
            ): null}
          </Box>
        </Paper>
      </Grow>
    </Grid>
    {/* </Container> */}
    </Grid>
    <DialogModal open={isDialogOpen} onClose={handleDialogClose} severity={dialogOptions.severity} title={dialogOptions.title} buttons={dialogOptions.buttons} action={dialogOptions.action}>
      {dialogOptions.message}
    </DialogModal> 
    <Snackbar
      open={isSnackbarOpen}
      autoHideDuration={3000}
      onClose={handleSnackbarClose}
      message={snackbarText}
    />
    </>
  );
};

export default ResetPassword;
